import * as React from 'react';
import useSWR from 'swr';

import type {
  LiveCreditBalance,
  PublicLiveSession,
} from '@youga/youga-interfaces';

import { useYougaClientApi } from '../YougaClientApiProvider';
import { isLiveFeatureEnabled, normalizeLiveSessions } from './liveSessionData';
import { ResponseError } from '../fetcher';

interface LiveSessionsResponse {
  enabled: boolean;
  rows: PublicLiveSession[];
}

interface JoinLiveSessionResponse {
  provider: string;
  joinUrl: string;
  balance: number;
}

export const getLiveJoinErrorCode = (error: unknown): string | undefined => {
  if (!(error instanceof ResponseError)) return undefined;
  const body = error.body as { code?: unknown } | undefined;
  return typeof body?.code === 'string' ? body.code : undefined;
};

export function useLiveSessions() {
  const { publicFetcher, fetcher } = useYougaClientApi();
  const response = useSWR<LiveSessionsResponse>('/live', {
    fetcher: publicFetcher,
    dedupingInterval: 30_000,
    refreshInterval: 60_000,
  });
  const featureEnabled = isLiveFeatureEnabled(response.data);
  const credits = useSWR<LiveCreditBalance>(
    featureEnabled ? '/live/credits' : null,
    {
      fetcher,
      dedupingInterval: 10_000,
      refreshInterval: 15_000,
    },
  );
  const sessions = normalizeLiveSessions(response.data);

  const joinLiveSession = React.useCallback(
    async (sessionId: string) =>
      fetcher<JoinLiveSessionResponse>(
        `/live/${encodeURIComponent(sessionId)}/join`,
        { method: 'POST' },
      ),
    [fetcher],
  );

  return {
    ...response,
    featureEnabled,
    data: sessions,
    refresh: response.mutate,
    balance: credits.data?.balance || 0,
    joinedSessionIds: credits.data?.joinedSessionIds || [],
    isBalanceLoading: credits.data == null && credits.error == null,
    refreshBalance: credits.mutate,
    joinLiveSession,
  };
}
